import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@org/ui';
import { useTranslation } from 'react-i18next';

type ReshuffleDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventDate: string;
  newClassName: string;
  onConfirm: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
};

/**
 * Confirmation dialog shown before reshuffling classes on a future
 * recurring-series occurrence (locked decision D-4).
 *
 * Assigning a new class to the target event causes all later events in the
 * same series to cascade by one slot. The class previously on the last
 * occurrence is pushed off the schedule.
 */
export function ReshuffleDialog({
  open,
  onOpenChange,
  eventDate,
  newClassName,
  onConfirm,
  onCancel,
  isLoading,
}: ReshuffleDialogProps) {
  const { t } = useTranslation('admin');

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('teacher.reshuffle.title')}</DialogTitle>
          <DialogDescription>
            {t('teacher.reshuffle.description', {
              date: eventDate,
              className: newClassName,
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4 flex gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            {t('teacher.reshuffle.cancel')}
          </Button>
          <Button
            onClick={() => onConfirm()}
            isLoading={isLoading}
            loadingText={t('common.status.saving')}
          >
            {t('teacher.reshuffle.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ReshuffleDialog;
